"use client";

import { PropsWithChildren, useEffect, useState, type FC } from "react";
import { XIcon, PlusIcon, FileText } from "lucide-react";
import {
  AttachmentPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  useAuiState,
} from "@assistant-ui/react";
import { useShallow } from "zustand/shallow";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const useFileSrc = (file: File | undefined) => {
  const [src, setSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!file) {
      setSrc(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return src;
};

const useAttachmentSrc = () => {
  const { file, src } = useAuiState(
    useShallow(({ attachment }): { file?: File; src?: string } => {
      if (attachment.type !== "image") return {};
      if (attachment.file) return { file: attachment.file };
      const src = attachment.content?.filter((c) => c.type === "image")[0]
        ?.image;
      if (!src) return {};
      return { src };
    }),
  );

  return useFileSrc(file) ?? src;
};

type AttachmentPreviewProps = {
  src: string;
};

const AttachmentPreview: FC<AttachmentPreviewProps> = ({ src }) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  return (
    <img
      src={src}
      alt={t("attachments.imagePreview")}
      className={cn(
        "block h-auto max-h-[80vh] w-auto max-w-full object-contain",
        isLoaded
          ? "aui-attachment-preview-image-loaded"
          : "aui-attachment-preview-image-loading invisible",
      )}
      onLoad={() => setIsLoaded(true)}
    />
  );
};

const AttachmentPreviewDialog: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const src = useAttachmentSrc();

  if (!src) return children;

  return (
    <Dialog>
      <DialogTrigger
        className="aui-attachment-preview-trigger cursor-pointer transition-colors hover:bg-accent/50"
        asChild
      >
        {children}
      </DialogTrigger>
      <DialogContent className="aui-attachment-preview-dialog-content p-2 sm:max-w-3xl [&>button]:rounded-full [&>button]:bg-foreground/60 [&>button]:p-1 [&>button]:opacity-100 [&>button]:ring-0! [&_svg]:text-background [&>button]:hover:[&_svg]:text-destructive">
        <DialogTitle className="aui-sr-only sr-only">
          {t("attachments.imagePreview")}
        </DialogTitle>
        <div className="aui-attachment-preview relative mx-auto flex max-h-[80dvh] w-full items-center justify-center overflow-hidden bg-background">
          <AttachmentPreview src={src} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AttachmentThumb: FC = () => {
  const { t } = useTranslation();
  const isImage = useAuiState(
    ({ attachment }) => attachment.type === "image",
  );
  const src = useAttachmentSrc();

  return (
    <Avatar className="aui-attachment-tile-avatar h-full w-full rounded-none">
      <AvatarImage
        src={src}
        alt={t("attachments.imagePreview")}
        className="aui-attachment-tile-image object-cover"
      />
      <AvatarFallback delayMs={isImage ? 200 : 0}>
        <FileText className="aui-attachment-tile-fallback-icon size-8 text-muted-foreground" />
      </AvatarFallback>
    </Avatar>
  );
};

// ─── Helpers for the compact pill layout ─────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1_048_576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

type TypeBadge = { label: string; className: string };

function resolveTypeBadge(fileName: string | undefined, rawType: string): TypeBadge {
  const ext = fileName?.split(".").pop()?.toLowerCase();
  if (ext === "pdf")
    return { label: "PDF", className: "bg-red-100 text-red-700" };
  if (ext === "doc" || ext === "docx")
    return { label: "DOC", className: "bg-blue-100 text-blue-700" };
  if (ext === "xls" || ext === "xlsx")
    return { label: "XLS", className: "bg-green-100 text-green-700" };
  if (rawType === "image")
    return { label: "IMG", className: "bg-purple-100 text-purple-700" };
  return { label: "FILE", className: "bg-muted-foreground/15 text-muted-foreground" };
}

// ─── Message-side attachment (unchanged classic tile) ────────────────────────

const MessageAttachmentUI: FC = () => {
  const { t } = useTranslation();
  const isImage = useAuiState(
    ({ attachment }) => attachment.type === "image",
  );
  const rawType = useAuiState(({ attachment }) => attachment.type);
  const typeLabel = (() => {
    switch (rawType) {
      case "image":  return t("attachments.typeImage");
      case "document": return t("attachments.typeDocument");
      default:       return t("attachments.typeFile");
    }
  })();

  return (
    <Tooltip>
      <AttachmentPrimitive.Root
        className={cn(
          "aui-attachment-root relative",
          isImage && "aui-attachment-root-composer only:[&>#attachment-tile]:size-24",
        )}
      >
        <AttachmentPreviewDialog>
          <TooltipTrigger asChild>
            <div
              className="aui-attachment-tile size-14 cursor-pointer overflow-hidden rounded-[14px] border bg-muted transition-opacity hover:opacity-75"
              role="button"
              id="attachment-tile"
              aria-label={`${typeLabel} attachment`}
            >
              <AttachmentThumb />
            </div>
          </TooltipTrigger>
        </AttachmentPreviewDialog>
      </AttachmentPrimitive.Root>
      <TooltipContent side="top">
        <AttachmentPrimitive.Name />
      </TooltipContent>
    </Tooltip>
  );
};

// ─── Composer-side compact pill attachment ────────────────────────────────────

const ComposerAttachmentUI: FC = () => {
  const { t } = useTranslation();
  const isImage = useAuiState(({ attachment }) => attachment.type === "image");
  const rawType = useAuiState(({ attachment }) => attachment.type);
  const fileName = useAuiState(({ attachment }) => attachment.name);
  const fileSize = useAuiState(({ attachment }) => (attachment as { file?: File }).file?.size);

  const badge = resolveTypeBadge(fileName, rawType);
  const src = useAttachmentSrc();

  return (
    <AttachmentPrimitive.Root className="aui-attachment-root relative">
      <AttachmentPreviewDialog>
        <div
          className="aui-attachment-pill flex items-center gap-2 rounded-xl border border-foreground/20 bg-muted px-2 py-1.5 h-14 cursor-pointer transition-opacity hover:opacity-80"
          role="button"
          aria-label={`${fileName ?? badge.label} attachment`}
        >
          {isImage && src ? (
            <img
              src={src}
              alt={t("attachments.imagePreview")}
              className="size-10 rounded-lg object-cover shrink-0"
            />
          ) : (
            <span className={cn(
              "flex items-center justify-center size-10 rounded-lg text-[10px] font-bold uppercase shrink-0",
              badge.className,
            )}>
              {badge.label}
            </span>
          )}
          <div className="flex flex-col gap-0.5 min-w-0 max-w-[10rem]">
            <span className="text-xs font-medium text-foreground truncate">
              {fileName ?? t("attachments.typeFile")}
            </span>
            {fileSize != null && (
              <span className="text-[10px] text-muted-foreground leading-none">
                {formatFileSize(fileSize)}
              </span>
            )}
          </div>
        </div>
      </AttachmentPreviewDialog>
      <AttachmentRemove />
    </AttachmentPrimitive.Root>
  );
};

const AttachmentRemove: FC = () => {
  const { t } = useTranslation();
  return (
    <AttachmentPrimitive.Remove asChild>
      <TooltipIconButton
        tooltip={t("attachments.removeFile")}
        className="aui-attachment-tile-remove absolute -top-1.5 -right-1.5 size-5 rounded-full bg-white text-muted-foreground opacity-100 shadow-sm hover:bg-white! [&_svg]:text-black hover:[&_svg]:text-destructive"
        side="top"
      >
        <XIcon className="aui-attachment-remove-icon size-3 dark:stroke-[2.5px]" />
      </TooltipIconButton>
    </AttachmentPrimitive.Remove>
  );
};

export const UserMessageAttachments: FC = () => {
  return (
    <div className="aui-user-message-attachments-end col-span-full col-start-1 row-start-1 flex w-full flex-row justify-end gap-2">
      <MessagePrimitive.Attachments components={{ Attachment: MessageAttachmentUI }} />
    </div>
  );
};

export const ComposerAttachments: FC = () => {
  return (
    <div className="aui-composer-attachments mb-2 flex w-full flex-row items-center gap-2 overflow-x-auto px-3 pt-3 pb-1 empty:hidden">
      <ComposerPrimitive.Attachments
        components={{ Attachment: ComposerAttachmentUI }}
      />
    </div>
  );
};

export const ComposerAddAttachment: FC = () => {
  const { t } = useTranslation();
  return (
    <ComposerPrimitive.AddAttachment asChild>
      <TooltipIconButton
        tooltip={t("attachments.addAttachment")}
        side="bottom"
        variant="ghost"
        size="icon"
        className="aui-composer-add-attachment size-8.5 rounded-full p-1 font-semibold text-xs hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30"
        aria-label={t("attachments.addAttachment")}
      >
        <PlusIcon className="aui-attachment-add-icon size-5 stroke-[1.5px]" />
      </TooltipIconButton>
    </ComposerPrimitive.AddAttachment>
  );
};
